var _excluded = ["color", "className", "disabled", "isDarkBackground", "tooltipText", "size"];
function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React from 'react';
import clsx from 'clsx';
import { bool, object, oneOf, string } from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import MuiButton from '@mui/material/Button';
import SovosTooltip from '../sovos-tooltip';
import { isKnownMaterialUiColor } from '../internals/utils/materialUiHelpers';
import { getAfterFocusStyles } from '../internals/utils';
import buttonStyles from '../internals/utils/buttonStyles';
var boxShadow = buttonStyles.boxShadow;
function getButtonColor(backgroundColor, color, palette, variant) {
  if (color === 'error') return palette.error.main;
  var foreBgColor = variant === 'contained' ? backgroundColor : color;
  return isKnownMaterialUiColor(foreBgColor) || !foreBgColor ? palette.primary.main : foreBgColor;
}
var ButtonRoot = styled(MuiButton, {
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'backgroundColor' && prop !== 'dark' && prop !== 'fgColor';
  }
})(function (_ref) {
  var backgroundColor = _ref.backgroundColor,
    dark = _ref.dark,
    disabled = _ref.disabled,
    fgColor = _ref.fgColor,
    size = _ref.size,
    _ref$theme = _ref.theme,
    palette = _ref$theme.palette,
    shape = _ref$theme.shape,
    spacing = _ref$theme.spacing,
    typography = _ref$theme.typography,
    variant = _ref.variant;
  var minHeight = '';
  if (size === 'small') {
    minHeight = spacing(3);
  } else if (size === 'medium') {
    minHeight = spacing(4);
  } else if (size === 'large') {
    minHeight = spacing(6);
  }
  var buttonColor = getButtonColor(backgroundColor, fgColor, palette, variant);
  var textColor = isKnownMaterialUiColor(fgColor) || !fgColor || fgColor === 'error' ? palette.primary.contrastText : fgColor;
  var getDarkOverrides = function getDarkOverrides() {
    var contrast = palette.text.contrast;
    var danger = fgColor === 'error';
    var getDarkText = function getDarkText() {
      if (disabled) return palette.text.disabled;
      if (danger) return palette.error.main;
      if (variant === 'outlined' || variant === 'text') return contrast;
      return palette.primary.main;
    };
    var getDarkOutline = function getDarkOutline() {
      if (disabled) return palette.action.disabledBackground;
      if (danger) return palette.error.main;
      return contrast;
    };
    var darkHoverBg = danger ? alpha(palette.error.main, palette.action.hoverOpacity) : alpha(contrast, palette.action.hoverOpacity);
    return {
      '&.MuiButton-containedPrimary': {
        color: getDarkText(),
        backgroundColor: disabled ? palette.grey[50] : palette.primary.contrastText,
        boxShadow: 'none',
        '&:hover': {
          color: palette.primary.contrastText,
          backgroundColor: danger ? palette.error.main : palette.primary.light,
          boxShadow: 'none'
        }
      },
      '&.MuiButton-outlinedPrimary': {
        color: getDarkText(),
        borderColor: getDarkOutline(),
        '&:hover': {
          backgroundColor: darkHoverBg
        }
      },
      '&.MuiButton-textPrimary': {
        color: getDarkText(),
        '&:hover': {
          backgroundColor: darkHoverBg
        }
      }
    };
  };
  var defaultOverrides = disabled ? {
    '&.MuiButtonBase-root.Mui-disabled': {
      color: palette.text.disabled,
      boxShadow: variant === 'contained' ? boxShadow : undefined
    }
  } : {
    '&.MuiButton-containedPrimary': {
      color: textColor,
      backgroundColor: buttonColor,
      '&:hover': {
        color: textColor,
        backgroundColor: fgColor === 'error' ? palette.error.dark : palette.primary.dark,
        boxShadow: boxShadow
      }
    },
    '&.MuiButton-outlinedPrimary': {
      color: buttonColor,
      borderColor: buttonColor,
      '&:hover': {
        borderColor: buttonColor,
        backgroundColor: alpha(buttonColor, palette.action.hoverOpacity)
      }
    },
    '&.MuiButton-textPrimary': {
      color: buttonColor,
      '&:hover': {
        backgroundColor: alpha(buttonColor, palette.action.hoverOpacity)
      }
    }
  };
  var variantOverrides = dark ? getDarkOverrides() : defaultOverrides;
  return _objectSpread(_objectSpread({
    minHeight: minHeight,
    borderRadius: shape.borderRadius,
    boxShadow: variant === 'contained' ? boxShadow : undefined,
    minWidth: spacing(9),
    padding: size === 'small' ? "".concat(spacing(0), " ").concat(spacing(2)) : "".concat(spacing(0.75), " ").concat(spacing(2)),
    '&:focus-visible': _objectSpread({
      boxShadow: 'none'
    }, getAfterFocusStyles())
  }, variantOverrides), typography.button);
});
var SovosButton = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var color = props.color,
    className = props.className,
    disabled = props.disabled,
    isDarkBackground = props.isDarkBackground,
    tooltipText = props.tooltipText,
    size = props.size,
    rest = _objectWithoutProperties(props, _excluded);
  var button = /*#__PURE__*/React.createElement(ButtonRoot, _extends({
    className: clsx('sovosButton', className),
    color: isKnownMaterialUiColor(color) ? color : 'primary',
    dark: isDarkBackground,
    disabled: disabled,
    disableFocusRipple: true,
    fgColor: color,
    size: size,
    ref: ref
  }, rest));
  return tooltipText ? /*#__PURE__*/React.createElement(SovosTooltip, {
    title: tooltipText
  }, disabled ? /*#__PURE__*/React.createElement("span", null, button) : button) : button;
});
SovosButton.propTypes = {
  /**
   * Override background color
   */
  backgroundColor: string,
  /**
   * Override or extend the styles applied to the component
   */
  classes: object,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  // TODO:
  // color: oneOf(['primary', 'inherit', 'default', 'error']),
  /**
   * One of the Material UI theme colors, `primary`, `default`, etc., or
   * `error`, or a custom color
   */
  color: string,
  /**
   * If `true`, the button is disabled
   */
  disabled: bool,
  /**
   * If used on a dark background
   */
  isDarkBackground: bool,
  /**
   * The size of the component.
   */
  size: oneOf(['small', 'medium', 'large']),
  /**
   * Tooltip text
   */
  tooltipText: string,
  /**
   * Button variant
   */
  variant: oneOf(['contained', 'outlined', 'text'])
};
SovosButton.defaultProps = {
  backgroundColor: undefined,
  classes: undefined,
  className: undefined,
  color: 'primary',
  disabled: false,
  isDarkBackground: false,
  size: 'medium',
  tooltipText: undefined,
  variant: 'contained'
};
SovosButton.baseComponent = {
  name: 'Button',
  link: 'button/'
};
export default SovosButton;